/* eslint-disable no-restricted-properties -- Settings are read in middleware, before app env initialization. */

import { DEFAULT_LOCALE, LOCALES, PSEUDO_LOCALE, isLocale, type Locale } from './locales';

export { DEFAULT_LOCALE, LOCALES, PSEUDO_LOCALE, isLocale, type Locale };

/**
 * Locales that get a URL prefix (`/es/...`). English stays unprefixed and the
 * pseudo-locale is a QA tool, not a public URL.
 */
export const PREFIX_LOCALES = ['es', 'fr'] as const satisfies readonly Locale[];
export type PrefixLocale = (typeof PREFIX_LOCALES)[number];

export const LOCALE_COOKIE = 'pb_locale';
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const LOCALE_HEADER = 'x-pb-locale';
export const LOCALE_SOURCE_HEADER = 'x-pb-locale-source';
export const LOCALE_SUGGESTED_HEADER = 'x-pb-locale-suggested';

/** Where the resolved locale came from. */
export type LocaleSource = 'prefix' | 'profile' | 'cookie' | 'geo' | 'default';

/** `manual` = the user picked it (or visited a prefixed URL); `geo` = guessed from country. */
export type CookieSource = 'manual' | 'geo';

const PREFIX_LOCALE_SET: ReadonlySet<string> = new Set(PREFIX_LOCALES);

export const isPrefixLocale = (value: unknown): value is PrefixLocale =>
  typeof value === 'string' && PREFIX_LOCALE_SET.has(value);

const LOCALE_SOURCES: ReadonlySet<string> = new Set([
  'prefix',
  'profile',
  'cookie',
  'geo',
  'default',
]);

export const isLocaleSource = (value: unknown): value is LocaleSource =>
  typeof value === 'string' && LOCALE_SOURCES.has(value);

/**
 * How the visitor's country is used on a first visit (no cookie, no profile
 * language, no prefix).
 *
 * - `off`: ignored.
 * - `suggest`: render English, expose the detected language in a header.
 * - `redirect`: 307 to the prefixed URL (needs `prefixRouting`, else `suggest`).
 * - `render`: render the detected language in place.
 */
export type CountryDetection = 'off' | 'suggest' | 'redirect' | 'render';

/** Values of `I18N_COUNTRY_DETECTION`, for the apps' env schemas (`choices`). */
export const COUNTRY_DETECTION_VALUES = [
  'off',
  'suggest',
  'redirect',
  'render',
] as const satisfies readonly CountryDetection[];

/** Values of `I18N_PREFIX_ROUTING`, for the apps' env schemas (`choices`). */
export const PREFIX_ROUTING_VALUES = ['on', 'off'] as const;

const LOCALE_ALTERNATIVES = LOCALES.join('|');

/**
 * `I18N_LOCALES` for the apps' env schemas (`match`): a comma-separated list of known locales
 * that includes `en`. `getI18nSettings` ignores anything else, so a typo would otherwise
 * silently fall back to English only.
 */
export const I18N_LOCALES_PATTERN = new RegExp(
  `^(?=(?:.*,)?\\s*${DEFAULT_LOCALE}\\s*(?:,|$))` +
    `\\s*(?:${LOCALE_ALTERNATIVES})\\s*(?:,\\s*(?:${LOCALE_ALTERNATIVES})\\s*)*$`
);

const COUNTRY_DETECTIONS: ReadonlySet<string> = new Set(COUNTRY_DETECTION_VALUES);

export const isCountryDetection = (value: unknown): value is CountryDetection =>
  typeof value === 'string' && COUNTRY_DETECTIONS.has(value);

export interface I18nSettings {
  /** Enabled locales. Always contains `en`. */
  locales: Locale[];
  /** Whether `/es/...` and `/fr/...` URLs are handled. */
  prefixRouting: boolean;
  countryDetection: CountryDetection;
}

/**
 * Settings come only from the `I18N_*` environment variables, read on every
 * call, on top of the defaults. Invalid values are ignored.
 *
 * Defaults are the same in every environment: English only, no prefix routing.
 * Other languages are switched on per environment (`I18N_LOCALES`,
 * `I18N_PREFIX_ROUTING`), so nothing partially translated ships by accident.
 */
export const getI18nSettings = (): I18nSettings => {
  const settings: I18nSettings = {
    locales: [DEFAULT_LOCALE],
    prefixRouting: false,
    countryDetection: 'suggest',
  };

  const envPrefix = process.env.I18N_PREFIX_ROUTING;
  if (envPrefix === 'on') settings.prefixRouting = true;
  if (envPrefix === 'off') settings.prefixRouting = false;

  const envLocales = process.env.I18N_LOCALES;
  if (envLocales) {
    const parsed = envLocales
      .split(',')
      .map((part) => part.trim())
      .filter(isLocale);
    if (parsed.includes(DEFAULT_LOCALE)) settings.locales = [...new Set(parsed)];
  }

  const envDetection = process.env.I18N_COUNTRY_DETECTION;
  if (isCountryDetection(envDetection)) settings.countryDetection = envDetection;

  // English must always be available as the fallback.
  if (!settings.locales.includes(DEFAULT_LOCALE)) {
    settings.locales = [DEFAULT_LOCALE, ...settings.locales];
  }

  // The pseudo-locale is a QA tool: production never serves it, whatever the env says.
  if (process.env.NEXT_PUBLIC_APP_ENV === 'prod') {
    settings.locales = settings.locales.filter((locale) => locale !== PSEUDO_LOCALE);
  }

  return settings;
};

/**
 * Cookie value format: `<locale>|<source>`, e.g. `es|manual`. Returns
 * `undefined` for anything malformed or for an unknown locale. A bare locale
 * (`es`) is read as a manual choice.
 */
export const parseLocaleCookie = (
  value: string | undefined
): { locale: Locale; source: CookieSource } | undefined => {
  if (!value) return undefined;
  const [locale, source = 'manual'] = value.split('|');
  if (!isLocale(locale)) return undefined;
  if (source !== 'manual' && source !== 'geo') return undefined;
  return { locale, source };
};

export const serializeLocaleCookie = (locale: Locale, source: CookieSource): string =>
  `${locale}|${source}`;
